import { Elements, injectStripe } from 'react-stripe-elements';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import FormTextField from 'components/formComponents/FormTextField';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  selectPoNumber,
  selectFinanceEmail,
  selectPrice,
  selectRegistrationToken,
  selectSelectedPackages,
  selectValidPromoCode,
} from 'reducers/misc';
import { ThreeBounce } from 'better-react-spinkit';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { useLocation, useNavigate } from '@reach/router';
import { AppPath } from 'appConstants';
import { useToast } from 'hooks/useToast';
import { formatNumber } from 'utils/formating';
import App from 'App';
import { SubscribeApi } from 'api/subscribeApi';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';

type PaymentDetailsFormProps = { onSubmit: () => void };

type InnerPaymentDetailsFormProps = {
  onSubmit: () => void;
  stripe?: any;
};

const InnerPaymentDetailsForm: React.FC<InnerPaymentDetailsFormProps> = ({
  onSubmit,
  stripe,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const handleToast = useToast('Your payment was successful');

  const [error, setError] = useState<string>('');
  const subscriptionPrice = useSelector(selectPrice);
  const promoCode = useSelector(selectValidPromoCode);
  const priceInfo = useSelector(selectPrice);
  const selectedPackages = useSelector(selectSelectedPackages);
  const registrationToken = useSelector(selectRegistrationToken);
  const poNumber = useSelector(selectPoNumber);
  const FinanceEmail = useSelector(selectFinanceEmail);

  const initialValues = {
    cardholderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
  };

  const validationSchema = Yup.object({
    cardholderName: Yup.string().required("Please enter the cardholder's name"),
  });

  const createStripeToken = (
    values: any,
    { setSubmitting }: FormikHelpers<any>
  ) =>
    stripe
      .createToken({ name: values.cardholderName })
      .then(({ token, error }: any) => {
        if (token) {
          upgradeByCard(token, setSubmitting);
        }
        if (error) {
          setSubmitting(false);
          setError('Sorry, something went wrong. Please try again.');
        }
      });

  const upgradeByCard = async (
    token: any,
    setSubmitting: (status: boolean) => void
  ) => {
    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.subscribeByCard(
        promoCode,
        selectedPackages,
        priceInfo.total,
        token,
        registrationToken,
        poNumber,
        FinanceEmail
      );
    } else {
      result = await NewSubscriptionApi.subscribeByCard(
        promoCode,
        selectedPackages,
        priceInfo.total,
        token,
        poNumber,
        FinanceEmail
      );
    }

    if (result.data) {
      const status = result.data.status;
      if (status === 'succeeded') {
        handleToast();
        AppPath.SUBSCRIBE === pathname
          ? onSubmit()
          : navigate(AppPath.DASHBOARD);
      }

      if (status === 'requires_action') {
        const clientSecret = result.data.clientSecret;
        const tid = result.data.tid;
        handleCardAction(clientSecret, setSubmitting, tid);
      }
    }
    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    }
  };

  const handleCardAction = (
    clientSecret: string,
    setSubmitting: (status: boolean) => void,
    tid: string
  ) => {
    stripe.handleCardAction(clientSecret).then((res: any) => {
      updateAfte3DSecure(res, tid, setSubmitting);
    });
  };

  const updateAfte3DSecure = async (
    values: any,
    tid: string,
    setSubmitting: (status: boolean) => void
  ) => {
    let result;

    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.updateAfter3DSecure({
        registrationToken,
        ...values,
        tid,
      });
    } else {
      result = await NewSubscriptionApi.updateAfter3DSecure({
        ...values,
        tid,
      });
    }
    if (result.data) {
      AppPath.SUBSCRIBE === pathname ? onSubmit() : navigate(AppPath.DASHBOARD);
    }
    if (result.error) {
      setSubmitting(false);
      setError(result.error.message);
    } else {
      AppPath.SUBSCRIBE === pathname ? onSubmit() : navigate(AppPath.DASHBOARD);
    }
  };

  const handleFormSubmition = (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    createStripeToken(values, formikHelpers);
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) =>
          handleFormSubmition(values, formikHelpers)
        }
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="PricingColRow">
              <div className="PCRowInner">
                <div className="LeftCol">
                  <FormTextField
                    field={'cardholderName'}
                    label={"Cardholder's name"}
                    required={true}
                  />
                  <FormStripeCardField
                    field={'card'}
                    label={'Card '}
                    required={true}
                  />
                  {/* <div className="SideBySideDifferent">
                    <FormTextField
                      field={'expiryDate'}
                      label={'Expiry date'}
                      placeholder={'mm/yyyy'}
                      required={true}
                    />
                    <FormTextField field={'cvv'} label={'CVV'} required={true} />
                  </div> */}
                </div>
                <div className="RightCol LowerBlock">
                  <PricingSummaryDetails 
                    addPromoStep={false}
                    isDisabled={isSubmitting}
                    isSubmitting={isSubmitting}
                    btnText="Subscribe"
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const PaymentDetailsFormWithStripe = injectStripe(InnerPaymentDetailsForm);

const PaymentDetailsForm: React.FC<PaymentDetailsFormProps> = (props) => {
  return (
    <Elements>
      <PaymentDetailsFormWithStripe {...props} />
    </Elements>
  );
};
export default PaymentDetailsForm;
